import { Box, Checkbox, IconButton, ListItem, Stack, Typography, styled } from '@mui/material'
import DropdownModal from 'components/runs/DropdownModal'
import PrimaryButton from 'components/runs/PrimaryButton'
import DropdownArrowIcon from 'components/shared/icons/DropdownArrowIcon'
import { CellImageAction } from 'components/shared/CellImageControl'
import { useState, useCallback } from 'react'
import { CellResponse } from 'utils/api'
import { useAuth } from 'services/auth'
import { useNotificationSlice } from 'redux/slices/hooks'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import useGlobalDownloadTrigger from 'utils/useGlobalDownloadTrigger'
import useRateLimiter from 'utils/useRateLimiter'
import { generateCellImagesZip, generateZipFileName } from 'utils/zipGenerator'
import useCellBrowsingSlice from 'redux/slices/hooks/useCellBrowsingSlice'
import moment from 'moment'
import { SampleTypeEncoderDecoder } from 'utils/proto-utils'
import { getPredictionValue } from './tableView/metadata'

const DOWNLOAD_ZIP_CONFIG = {
  maxCellIds: 10,
  downloadLimit: 10,
  timeFrame: 10, // In-minutes
}

const StyledListItem = styled(ListItem)({
  color: '#000',
  fontSize: '12px',
  fontWeight: 'bold',
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  '&:hover': {
    background: '#E6E4FF',
  },
  '&:active': {
    color: '#5F55D1',
    background: '#E6E4FF',
  },
  '& > a': {
    textDecoration: 'none',
    color: '#000',
  },
})

interface CellBrowserExportProps {
  checkedCells: CellResponse[]
  handleQueryUpdate: (action: CellImageAction) => void
}

const CellBrowserExport = ({
  checkedCells,
  handleQueryUpdate,
}: CellBrowserExportProps): JSX.Element => {
  const [modalState, setModalState] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [checkboxState, setCheckboxState] = useState<boolean>(false)
  const { isInternalUser } = useAuth()
  const { showError } = useNotificationSlice()
  const events = useEventManager()

  const { triggerSyncDownload } = useGlobalDownloadTrigger()
  const {
    cellBrowsing: { selectedCells },
  } = useCellBrowsingSlice()

  // Rate limiters for image downloads
  const pngRateLimiter = useRateLimiter({
    key: 'cell-images-png-download',
    maxDownloads: DOWNLOAD_ZIP_CONFIG.downloadLimit,
    timeFrameMinutes: DOWNLOAD_ZIP_CONFIG.timeFrame,
  })

  const tiffRateLimiter = useRateLimiter({
    key: 'cell-images-tiff-download',
    maxDownloads: DOWNLOAD_ZIP_CONFIG.downloadLimit,
    timeFrameMinutes: DOWNLOAD_ZIP_CONFIG.timeFrame,
  })

  const handleExport = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setModalState(!modalState)
  }

  function CellReponseCSV(cells: CellResponse[]) {
    return cells.map(({ cell }) => {
      const cellId = cell.getCellId()
      const cellTime = cellId?.getTime()
      const instrumentId = cellId?.getInstrumentId()
      const cellNumber = cellId?.getNumber()

      return `${cellTime} #${cellNumber} ${instrumentId}`
    })
  }

  // Limit cells to first 10 for external users
  const cellsForExport =
    !isInternalUser && checkedCells.length > DOWNLOAD_ZIP_CONFIG.maxCellIds
      ? checkedCells.slice(0, DOWNLOAD_ZIP_CONFIG.maxCellIds)
      : checkedCells

  const handleImageExport = async (fileType: 'png' | 'tiff') => {
    // Show warning for external users when they have more than 10 cells selected
    if (!isInternalUser && checkedCells.length > DOWNLOAD_ZIP_CONFIG.maxCellIds) {
      showError(
        `Maximum of ${DOWNLOAD_ZIP_CONFIG.maxCellIds} cells can be exported as images. Only the first ${DOWNLOAD_ZIP_CONFIG.maxCellIds} cells will be exported.`,
        undefined,
        'warning'
      )
    }

    // Track cell browsing export
    events.sendCellBrowsingExport({
      cells: cellsForExport.map(({ cell }) => {
        const cellId = cell.getCellId()
        return {
          runId: cell.getRunId() || '',
          cellId: `${cellId?.getTime()} #${cellId?.getNumber()} ${cellId?.getInstrumentId()}`,
        }
      }),
      exportType: fileType,
    })

    // Convert cells to ID strings
    const cellIds = CellReponseCSV(cellsForExport)

    // Use rate limiter and trigger download
    const rateLimiter = fileType === 'png' ? pngRateLimiter : tiffRateLimiter
    await rateLimiter.checkAndExecute(async () => {
      const fileName = generateZipFileName(`[${fileType.toUpperCase()}]`)

      await triggerSyncDownload({
        type: fileType === 'png' ? 'cell-images-png' : 'cell-images-tiff',
        fileName,
        generateFile: async () => {
          const blob = await generateCellImagesZip(cellIds, fileType)
          return blob
        },
      })
    })

    // Close the modal
    setModalState(false)
  }

  const downloadCSV = useCallback(async () => {
    const headers = ['Cell Id', 'Run', 'Time', 'Sample', 'Mixed Sample', 'Sample Type', 'Number of Images', 'Image Size', 'Predictions']
    const data = selectedCells.map((selectedCell) => {
      const { cell } = selectedCell
      const imageList = cell.getImagesList() || []
      const cellId = cell.getCellId()
      return {
        'Cell Id': `${cellId?.getTime()} #${cellId?.getNumber()} ${cellId?.getInstrumentId()}`,
        'Run': cell.getRunId() || '-',
        'Time': moment.utc(cellId?.getTime()).local().format('YYYY-MM-DD h:mm:ss.SSS A') || '-',
        'Sample': cell.getSample()?.getSampleId() || '-',
        'Mixed Sample': cell.getSample()?.getMixedSampleId() || '-',
        'Sample Type': SampleTypeEncoderDecoder.convertToString(cell?.getSample()?.getSampleType()) || '-',
        'Number of Images': cell?.getNumImages() || '-',
        'Image Size': `${imageList[0]?.getBlobWidth()}x${imageList[0]?.getBlobHeight()}` || '-',
        'Predictions': getPredictionValue(cell) || '-',
      }
    })

    const csvContent = [
      headers.join(','),
      ...data.map((row) => headers.map((h) => {
        const value = String(row[h as keyof typeof row] ?? '')
        // Escape values with commas or quotes
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')),
    ].join('\n')

    await triggerSyncDownload({
      type: 'csv',
      fileName: 'CellBrowsing.csv',
      generateFile: () => csvContent,
    })
  }, [selectedCells, triggerSyncDownload])

  const downloadTSV = useCallback(async () => {
    const headers = ['Cell Id', 'Run', 'Time', 'Sample', 'Mixed Sample', 'Sample Type', 'Number of Images', 'Image Size', 'Predictions']
    const data = selectedCells.map((selectedCell) => {
      const { cell } = selectedCell
      const imageList = cell.getImagesList() || []
      const cellId = cell.getCellId()
      return {
        'Cell Id': `${cellId?.getTime()} #${cellId?.getNumber()} ${cellId?.getInstrumentId()}`,
        'Run': cell.getRunId() || '-',
        'Time': moment.utc(cellId?.getTime()).local().format('YYYY-MM-DD h:mm:ss.SSS A') || '-',
        'Sample': cell.getSample()?.getSampleId() || '-',
        'Mixed Sample': cell.getSample()?.getMixedSampleId() || '-',
        'Sample Type': SampleTypeEncoderDecoder.convertToString(cell?.getSample()?.getSampleType()) || '-',
        'Number of Images': cell?.getNumImages() || '-',
        'Image Size': `${imageList[0]?.getBlobWidth()}x${imageList[0]?.getBlobHeight()}` || '-',
        'Predictions': getPredictionValue(cell) || '-',
      }
    })

    const tsvContent = [
      headers.join('\t'),
      ...data.map((row) => headers.map((h) => row[h as keyof typeof row]).join('\t')),
    ].join('\n')

    await triggerSyncDownload({
      type: 'tsv',
      fileName: 'CellBrowsing.tsv',
      generateFile: () => tsvContent,
    })
  }, [selectedCells, triggerSyncDownload])

  return (
    <>
      <Box data-testid="export-dropdown" sx={{ padding: '22px 30px 20px 24px' }}>
        <Stack sx={{ height: '30px' }} direction="row" justifyContent="space-between">
          <Stack direction="row" gap="8px" alignItems="center">
            <Checkbox
              sx={{ p: 0 }}
              checked={checkboxState}
              indeterminate
              onChange={(event) => {
                if (event.target.checked) {
                  handleQueryUpdate({ type: 'resetCheckboxSelection' })
                }
                setCheckboxState(event.target.checked)
              }}
            />
            <Typography
              sx={{
                color: '#5F55D1',
                fontSize: '12px',
                lineHeight: '13px',
                letterSpacing: '0.1em',
              }}
            >
              {checkedCells.length} SELECTED
            </Typography>
          </Stack>
          <PrimaryButton sx={{ width: '118px' }} onClick={handleExport}>
            <Stack direction="row" gap="8px" alignItems="center">
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '13px',
                  letterSpacing: '0.1em',
                }}
              >
                Export
              </Typography>
              <IconButton sx={{ p: 0 }}>
                <DropdownArrowIcon sx={{ width: '18px', height: '18px', fill: '#5F55D1' }} />
              </IconButton>
            </Stack>
          </PrimaryButton>
        </Stack>
      </Box>
      <DropdownModal
        placement="bottom-start"
        disablePortal={false}
        transition
        anchorEl={anchorEl}
        open={modalState}
        onClickAway={() => setModalState(false)}
      >
        <Box sx={{ width: '118px' }}>
          <StyledListItem
            onClick={() => {
              handleImageExport('png')
            }}
          >
            PNG
          </StyledListItem>
          <StyledListItem
            onClick={() => {
              handleImageExport('tiff')
            }}
          >
            TIFF
          </StyledListItem>
          <StyledListItem
            onClick={() => {
              downloadCSV()
              setModalState(false)
            }}
          >
            CSV
          </StyledListItem>
          <StyledListItem
            onClick={() => {
              downloadTSV()
              setModalState(false)
            }}
          >
            TSV
          </StyledListItem>
        </Box>
      </DropdownModal>

      {/* Downloads are now handled by the Enhanced Download UX system */}
    </>
  )
}

export default CellBrowserExport
